import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import mixpanel from 'mixpanel-browser'
import React, { Dispatch, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SignUpModal } from 'src/components/global/SignUpModal'
import { MIXPANEL_ENABLED } from 'src/config/config'
import { IEnum_Solution_Type, useFollowSolutionMutation, useIsFollowLazyQuery } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { showToast } from 'src/store/actions/toastAction'
import { AppState } from 'src/store/types'

interface FollowButtonProps {
  id: string
  solutionType?: IEnum_Solution_Type | null
  displayName?: string | null
  isEnhanced?: boolean | null
  isButton?: boolean
  className?: string
  follow?: boolean
  isFromVendorList?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const FollowButton: React.FC<FollowButtonProps> = ({
  id,
  solutionType,
  displayName,
  isEnhanced = false,
  className,
  isButton = true,
  follow = false,
  isFromVendorList = false,
}) => {
  const { status } = useAuthenticated()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()

  const authInfo = useSelector((state: AppState) => state.authInfo)

  const [following, setFollowing] = useState(follow)
  const [showSignUp, setShowSignUp] = useState(false)
  const [loadIsFollowing, { data: isFollowingData }] = useIsFollowLazyQuery()
  const [followSolution] = useFollowSolutionMutation()

  useEffect(() => {
    if (!isFromVendorList) {
      !!authInfo.idToken && loadIsFollowing({ variables: { id: parseInt(id || '') } })
    }
  }, [authInfo])

  useEffect(() => {
    if (!isFromVendorList) {
      setFollowing(!!isFollowingData?.isFollow)
    }
  }, [isFollowingData])
  useEffect(() => {
    if (typeof follow === 'boolean') setFollowing(follow)
  }, [follow])
  const onHandleFollow = async () => {
    if (status !== 'authenticated') {
      setShowSignUp(true)
      return
    }

    if (authInfo.idToken !== undefined && authInfo.idToken !== '') {
      const isFollowing = !following
      const { data } = await followSolution({
        variables: {
          id: parseInt(id || ''),
          follow: isFollowing,
        },
      })
      if (data?.followSolution) {
        dispatch(
          showToast(
            isFollowing ? 'Added to your followed solutions' : 'Removed from your followed solutions',
            'confirm',
          ),
        )
        if (MIXPANEL_ENABLED) {
          if (isFollowing) {
            mixpanel.track('follow_product', {
              solutionType,
              id,
              displayName,
              isEnhanced,
            })
          } else {
            mixpanel.track('unfollow_product', {
              solutionType,
              id,
              displayName,
              isEnhanced,
            })
          }
        }
        setFollowing(isFollowing)
      }
    }
  }

  const handleCloseSignUp = () => {
    setShowSignUp(false)
  }

  return (
    <div className={cn('flex', className)}>
      {isButton ? (
        <button className="box-border h-[42px] px-[16px] py-[8px] w-full border-solid border-[1px] border-mist rounded-[25px] bg-white items-center">
          <div className="flex flex-row items-center self-center justify-center" onClick={onHandleFollow}>
            {following ? (
              <IonIcon name="star" className="flex items-center text-[#104268] text-[1.3rem] mr-[5px]" />
            ) : (
              <IonIcon name="star-outline" className="flex items-center text-[#104268] text-[1.3rem] mr-[5px]" />
            )}
            <span className="flex items-center h-full text-darkgray font-secondary text-[14px] font-[500]">
              {following ? 'Following' : 'Follow'}
            </span>
          </div>
        </button>
      ) : (
        <button className="flex flex-row items-center h-[1.5rem] ml-[1rem] w-[91px]" onClick={onHandleFollow}>
          {following ? (
            <IonIcon name="star" className="text-navy" />
          ) : (
            <IonIcon name="star-outline" className="text-textdark opacity-40" />
          )}

          <span className="pl-2 font-secondary font-medium text-[14px] text-darkgray">
            {following ? 'Following' : 'Follow'}
          </span>
        </button>
      )}

      <SignUpModal isOpen={showSignUp} onClose={handleCloseSignUp} />
    </div>
  )
}
